/**
 * @file AvitoSyncLogPage.tsx
 * @description Журнал запусков синхронизации Avito (phase14.0).
 */

import { Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { ApiError } from "../../../lib/api-client";
import { shopAvito, type AvitoSyncJobDto } from "../../../lib/api-shop-avito";

const TRIGGER_LABELS: Record<string, string> = {
  cron: "Автомат",
  manual: "Вручную",
  startup: "При старте",
};

const KIND_LABELS: Record<string, string> = {
  listings: "Листинги",
  orders: "Заказы",
  reviews: "Отзывы",
};

const STATUS_LABELS: Record<string, string> = {
  success: "Успех",
  error: "Ошибка",
  running: "Выполняется",
};

export function AvitoSyncLogPage() {
  const [items, setItems] = useState<AvitoSyncJobDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await shopAvito.syncJobs({ page: 1, perPage: 100 });
      setItems(r.items);
      setTotal(r.meta.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить журнал");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-on-surface-variant">
          Всего записей: <strong className="text-on-surface">{total}</strong>
        </p>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="size-4" />
          Обновить
        </Button>
      </div>

      {loading ? (
        <Loader2 className="size-5 animate-spin mx-auto my-16 text-on-surface-variant" />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body className="text-center py-10 text-on-surface-variant">
            Журнал пуст — синхронизации ещё не запускались.
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        <EditorialCard>
          <EditorialCard.Body className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-on-surface-variant border-b border-outline-variant">
                  <tr>
                    <th className="px-4 py-3">Тип</th>
                    <th className="px-4 py-3">Триггер</th>
                    <th className="px-4 py-3">Статус</th>
                    <th className="px-4 py-3 text-right">Обработано</th>
                    <th className="px-4 py-3 text-right">Создано</th>
                    <th className="px-4 py-3 text-right">Обновлено</th>
                    <th className="px-4 py-3">Начало</th>
                    <th className="px-4 py-3">Длит.</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((j) => (
                    <tr
                      key={j.id}
                      className="border-b border-outline-variant last:border-0 align-top"
                    >
                      <td className="px-4 py-3 font-medium">{KIND_LABELS[j.kind] ?? j.kind}</td>
                      <td className="px-4 py-3 text-on-surface-variant">
                        {TRIGGER_LABELS[j.trigger] ?? j.trigger}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            j.status === "success"
                              ? "success"
                              : j.status === "error"
                                ? "destructive"
                                : "default"
                          }
                        >
                          {STATUS_LABELS[j.status] ?? j.status}
                        </Badge>
                        {j.errorMessage && (
                          <div className="mt-1 text-xs text-destructive max-w-xs truncate">
                            {j.errorMessage}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{j.itemsProcessed}</td>
                      <td className="px-4 py-3 text-right">{j.itemsCreated}</td>
                      <td className="px-4 py-3 text-right">{j.itemsUpdated}</td>
                      <td className="px-4 py-3 text-on-surface-variant">
                        {new Date(j.startedAt).toLocaleString("ru-RU")}
                      </td>
                      <td className="px-4 py-3 text-on-surface-variant">
                        {j.finishedAt
                          ? `${Math.max(
                              0,
                              Math.round(
                                (new Date(j.finishedAt).getTime() -
                                  new Date(j.startedAt).getTime()) /
                                  1000,
                              ),
                            )}с`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </EditorialCard.Body>
        </EditorialCard>
      )}
    </div>
  );
}
